import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiErrorMessage, formatPLN, formatDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";
import { Sparkles, Repeat, TrendingUp, Plus, ShoppingCart, Wand2, ArrowRight, Info } from "lucide-react";

const TABS = [
  { key: "frequent", label: "Najczęściej zamawiane", Icon: TrendingUp, hint: "Twoje top produkty z historii" },
  { key: "last", label: "Powtórz ostatnie", Icon: Repeat, hint: "Skopiuj pozycje z ostatniego zamówienia" },
  { key: "auto", label: "Sugerowane przez system", Icon: Wand2, hint: "Propozycja na podstawie częstotliwości" },
];

export default function Suggestions() {
  const [tab, setTab] = useState("frequent");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const { addItem, count, total } = useCart();

  const load = async (key) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/orders/suggestions/${key}`);
      setItems(data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(tab); }, [tab]);

  const addSingle = (it) => {
    addItem(
      { id: it.product_id, name: it.product_name, sku: it.sku, unit: it.unit, price_net: it.unit_price_net },
      Number(it.suggested_quantity) || 1,
    );
    toast.success(`Dodano: ${it.product_name}`);
  };

  const addAll = () => {
    if (items.length === 0) return;
    items.forEach((it) => {
      addItem(
        { id: it.product_id, name: it.product_name, sku: it.sku, unit: it.unit, price_net: it.unit_price_net },
        Number(it.suggested_quantity) || 1,
      );
    });
    toast.success(`Dodano ${items.length} pozycji do koszyka`);
  };

  const suggestedTotal = items.reduce((s, i) => s + Number(i.unit_price_net) * Number(i.suggested_quantity), 0);
  const activeTab = TABS.find((t) => t.key === tab);

  return (
    <div>
      <PageHeader
        testId="suggestions-page"
        title="Sugestie zamówień"
        subtitle="Skróć drogę do złożenia zamówienia – wykorzystaj swoją historię"
        actions={
          <Link to="/cart" data-testid="suggestions-cart-link"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#FF6B1A] hover:bg-[#E65A10] text-white text-sm font-semibold transition-colors">
            <ShoppingCart className="w-4 h-4" /> Koszyk · {count} poz. · {formatPLN(total)}
          </Link>
        }
      />

      <div className="flex flex-wrap gap-2 mb-5" data-testid="suggestions-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
              tab === t.key
                ? "bg-[#FF6B1A] text-white shadow-sm"
                : "bg-white border border-slate-300 text-slate-700 hover:border-slate-400"
            }`}>
            <t.Icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      <div className="ol-card p-4 mb-4 flex items-center justify-between bg-slate-50">
        <div className="flex items-center gap-2 text-sm text-slate-700">
          <Sparkles className="w-4 h-4 text-[#FF6B1A]" />
          <span><strong>{activeTab.label}:</strong> {activeTab.hint}</span>
        </div>
        {items.length > 0 && (
          <button onClick={addAll} data-testid="add-all-btn"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold">
            <Plus className="w-3.5 h-3.5" /> Dodaj wszystkie ({items.length}) – {formatPLN(suggestedTotal)}
          </button>
        )}
      </div>

      <div className="ol-card overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500">Ładowanie sugestii…</div>
        ) : items.length === 0 ? (
          <EmptyState tab={tab} />
        ) : (
          <table className="ol-table" data-testid="suggestions-table">
            <thead>
              <tr>
                <th>SKU</th>
                <th>Produkt</th>
                <th>Jedn.</th>
                <th className="text-right">Cena netto</th>
                <th className="text-center">Sugerowana ilość</th>
                <th className="text-right">Wartość</th>
                {tab !== "last" && <th>Historia</th>}
                <th className="text-right">Akcja</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.product_id}>
                  <td className="font-mono text-xs text-slate-500">{it.sku}</td>
                  <td className="font-medium text-slate-900">{it.product_name}</td>
                  <td className="text-slate-600">{it.unit}</td>
                  <td className="text-right">{formatPLN(it.unit_price_net)}</td>
                  <td className="text-center font-semibold">{it.suggested_quantity}</td>
                  <td className="text-right font-semibold">{formatPLN(it.unit_price_net * it.suggested_quantity)}</td>
                  {tab !== "last" && (
                    <td>
                      <div className="text-xs text-slate-600">
                        {it.reason ? <span>{it.reason}</span> : (
                          <span>{it.times_ordered}× w historii · ostatnio {it.last_ordered_at ? formatDate(it.last_ordered_at) : "—"}</span>
                        )}
                      </div>
                    </td>
                  )}
                  <td className="text-right">
                    <button onClick={() => addSingle(it)} data-testid={`add-suggested-${it.sku}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-slate-300 hover:border-[#FF6B1A] hover:text-[#FF6B1A] text-xs font-semibold transition-colors">
                      <Plus className="w-3.5 h-3.5" /> Dodaj
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p className="mt-4 text-xs text-slate-500 flex items-center gap-1.5">
        <Info className="w-3.5 h-3.5" />
        Sugestie liczone są z Twojej historii zamówień. Brak danych = brak sugestii. <Link to="/catalog" className="ol-link">Przejdź do katalogu</Link>.
      </p>
    </div>
  );
}

function EmptyState({ tab }) {
  const messages = {
    frequent: "Nie zamówiłeś jeszcze żadnego produktu. Złóż pierwsze zamówienie, aby zobaczyć tutaj swoje topowe pozycje.",
    last: "Nie masz jeszcze żadnego wysłanego zamówienia, które można by powtórzyć.",
    auto: "Za mało historii, aby zaproponować zamówienie. System potrzebuje co najmniej kilku zamówień, by wykryć rytm zakupów.",
  };
  return (
    <div className="p-12 text-center text-sm text-slate-500">
      <Sparkles className="w-8 h-8 text-slate-300 mx-auto mb-3" />
      <p>{messages[tab]}</p>
      <Link to="/catalog" className="ol-link mt-3 inline-flex items-center gap-1 text-sm font-semibold">
        Przejdź do katalogu <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
}
